import type { FC } from "react";

import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { useTabParam } from "../../hooks/useTabParam";
import { useTeamsChatSdkEnabled } from "../../hooks/useTeamsChatSdkEnabled";
import { Alert } from "../common/Alert";
import { Tabs } from "../common/Tabs";
import { AdminAgentModeBudgetsTab } from "./AdminAgentModeBudgetsTab";
import { AdminFeatureFlagsTab } from "./AdminFeatureFlagsTab";
import { AdminHostFencingTab } from "./AdminHostFencingTab";
import { AdminKeystoreTab } from "./AdminKeystoreTab";
import { AdminOrganizationsTab } from "./AdminOrganizationsTab";
import { AdminSystemTab } from "./AdminSystemTab";
import { AdminTeamsTab } from "./AdminTeamsTab";
import { AdminUsersTab } from "./AdminUsersTab";

type AdminTab =
  | "organizations"
  | "users"
  | "budgets"
  | "feature-flags"
  | "host-fencing"
  | "keystore"
  | "system"
  | "teams";

const TABS: { id: AdminTab; label: string }[] = [
  { id: "organizations", label: "Organizations" },
  { id: "users", label: "Users" },
  { id: "budgets", label: "Budgets" },
  { id: "feature-flags", label: "Feature Flags" },
  { id: "host-fencing", label: "Host Fencing" },
  { id: "keystore", label: "Key Store" },
  { id: "system", label: "System" },
  { id: "teams", label: "Teams" },
];

function isAdminTab(value: string): value is AdminTab {
  return TABS.some((tab) => tab.id === value);
}

export const AdminPanel: FC = () => {
  const { globalRole } = useDashboardAuth();
  const [activeTab, setTab] = useTabParam<AdminTab>("organizations");
  const teamsChatSdk = useTeamsChatSdkEnabled("/admin/teams/setup");
  const tabs = teamsChatSdk.enabled
    ? TABS
    : TABS.filter((tab) => tab.id !== "teams");
  const selectedTab =
    activeTab === "teams" && !teamsChatSdk.enabled
      ? "organizations"
      : activeTab;

  if (globalRole !== "admin") {
    return (
      <Alert variant="error" title="Access Denied">
        You do not have permission to view this page. Platform admin access is
        required.
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs
        tabs={tabs}
        activeTab={selectedTab}
        onChange={(id) => {
          if (isAdminTab(id)) {
            setTab(id);
          }
        }}
      />

      {selectedTab === "organizations" && <AdminOrganizationsTab />}
      {selectedTab === "users" && <AdminUsersTab />}
      {selectedTab === "budgets" && <AdminAgentModeBudgetsTab />}
      {selectedTab === "feature-flags" && <AdminFeatureFlagsTab />}
      {selectedTab === "host-fencing" && <AdminHostFencingTab />}
      {selectedTab === "keystore" && <AdminKeystoreTab />}
      {selectedTab === "system" && <AdminSystemTab />}
      {selectedTab === "teams" && <AdminTeamsTab />}
    </div>
  );
};
